import { useQuery, useReactiveVar } from '@apollo/client';
import { createError } from '@common/neo.tax';

import { GetUserFromAuthQuery, GetUserFromAuthQueryVariables, GET_USER_FROM_AUTH } from '@neo.tax/shared/graphql';
import { AuthenticationState, VersionGuardState } from '@neo.tax/shared/state';

import { useUrlQueryParam } from './useUrlQueryParam';

export const useUserFromAuth = (): {
  loading: boolean;
  userId?: string;
  isSuperUserMode: boolean;
  // False when the session is authenticated in Firebase but has no DB user yet — an orphan that needs
  // to finish signup. (False, too, while loading / logged out; only meaningful once loading is done.)
  existsInDb: boolean;
} => {
  const { isVersionOutdated } = useReactiveVar(VersionGuardState.versionGuardVar);
  const {
    authenticationServiceState: { authenticationUser },
    isAuthenticating,
  } = useReactiveVar(AuthenticationState.authenticationStateVar);

  // Can only reconcile an authenticated user
  const firebaseUserId = authenticationUser?.uid;
  const getUserFromAuthOptions =
    !firebaseUserId || isAuthenticating ? { skip: true } : { variables: { firebaseUserId } };
  const {
    data: userAuthInfo,
    error: userAuthInfoError,
    loading: userAuthInfoLoading,
  } = useQuery<GetUserFromAuthQuery, GetUserFromAuthQueryVariables>(GET_USER_FROM_AUTH, getUserFromAuthOptions);

  const userId = useUrlQueryParam('userId');

  if (userAuthInfoLoading) {
    return {
      loading: true,
      isSuperUserMode: false,
      existsInDb: false,
    };
  }
  // Logged out state
  if (!firebaseUserId) {
    return {
      loading: false,
      isSuperUserMode: false,
      existsInDb: false,
    };
  }
  // User is authenticated in firebase but not in our system
  if (isAuthenticating) {
    return {
      loading: true,
      isSuperUserMode: false,
      existsInDb: false,
    };
  }
  if (userAuthInfoError) {
    throw userAuthInfoError;
  }
  if (!userAuthInfo) {
    throw createError('UndefinedUserAuthInfo', 'GetUserFromAuth query done loading and user auth info still undefined');
  }

  const dbUser = userAuthInfo.getUserFromAuth;
  // Authenticated in Firebase but not provisioned in our DB — an orphan that needs to finish signup.
  // Surfaced as existsInDb:false (rather than throwing) so useAuthRedirect can route them to /signup.
  if (!dbUser) {
    return {
      loading: false,
      isSuperUserMode: false,
      existsInDb: false,
    };
  }

  const { isSuperUser, userId: authenticatedUserId } = dbUser;

  return {
    userId: userId ?? authenticatedUserId,
    loading: authenticatedUserId ? false : userAuthInfoLoading || isVersionOutdated,
    isSuperUserMode: !!userId && isSuperUser && userId !== authenticatedUserId,
    existsInDb: true,
  };
};
